import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { first, isNumber } from 'lodash';
import { combineLatest, filter, map } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import {
  selectSelectedPortfolioId,
  selectSelectedPropertyIds,
} from '@dashboards/store/selectors/property-selector.selectors';
import { allPortfoliosValue } from '@main-application/application-menu/property-selector/constants';
import { getCurrentStep } from '@main-application/boards/interfaces/board';
import { BoardSourceType } from '@main-application/boards/interfaces/board.enums';
import { selectProperties } from '@portfolio/store/portfolio.selectors';
import { UnitIconList } from '@shared/constants/unit-condition-list.const';
import { WorkflowStepListActive } from '@shared/constants/workflow-step-list.const';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { getPortfolioProperties } from '@shared/functions/get-portfolio-properties.function';
import { InputModule } from '@ui-components/controls/input/input.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { ModalsService } from '@ui-components/modals/modals.service';

export interface NonPmsAddModalData {
  unitName: string;
  propertyId: number;
  unitIconType: string;
  startWorkflowStepType: WorkflowStepEnumType;
  datasource?: BoardSourceType;
  fromBoard?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-non-pms-add-modal',
  templateUrl: './non-pms-add-modal.component.html',
  styleUrls: ['./non-pms-add-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    InputModule,
    RadioButtonDropdownModule,
  ],
})
export class NonPmsAddModalComponent extends PuSubscribable implements OnInit {
  private modalService: ModalsService = inject(ModalsService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  public dialogRef: MatDialogRef<NonPmsAddModalComponent> = inject(MatDialogRef);
  private store: Store<{}> = inject(Store);
  public data: NonPmsAddModalData = inject(MAT_DIALOG_DATA);

  protected readonly UnitIconList = UnitIconList;

  form = this.formBuilder.group({
    propertyId: [null as number, [Validators.required]],
    unitName: ['non-PMS unitxxx', [Validators.required]],
    unitIconType: [null as string],
    startWorkflowStepType: [null as number],
  });

  formSubmited = false;
  acceptChargesText$ = this.modalService.getAcceptChargesText();

  userProperties$ = combineLatest([
    this.store.select(selectProperties),
    this.store.select(selectSelectedPortfolioId),
  ]).pipe(
    untilDestroyed(this),
    filter(([userProperties]) => !!userProperties),
    map(([userProperties, currentPortfolioId]) =>
      getPortfolioProperties(
        userProperties.filter(e => currentPortfolioId === allPortfoliosValue || e.portfolioId === currentPortfolioId)
      )
    )
  );
  workflowStepTypes = WorkflowStepListActive;

  constructor() {
    super();
    if (this.data) {
      this.form.controls.unitName.setValue(this.data.unitName);
    }
  }

  ngOnInit(): void {
    combineLatest([this.store.select(selectSelectedPropertyIds)])
      .pipe(untilDestroyed(this))
      .subscribe(([propertyIds]) => {
        if (this.data?.propertyId) {
          this.form.get('propertyId').setValue(this.data.propertyId);
        } else {
          this.form.controls.propertyId.setValue(first(propertyIds));
        }
      })
      .untilDestroyed(this);

    const sourceType = this.data?.datasource;
    if (isNumber(sourceType)) {
      const currentStep = getCurrentStep(sourceType, true);

      if (currentStep !== null) this.form.get('startWorkflowStepType').setValue(WorkflowStepEnumType.MakeReady);

      this.form.get('startWorkflowStepType').setValidators([Validators.required]);
      this.form.get('startWorkflowStepType').updateValueAndValidity();
    }
  }

  ok() {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    } else {
      this.formSubmited = true;
      this.form.markAllAsTouched();
    }
  }

  close() {
    this.dialogRef.close();
  }
}
